import { createContext, useContext, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const PlayerContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const PREVIEW_DURATION_MS = 30000;

const isUnreleased = (track) => {
  if (!track?.release_date) return false;
  return new Date(track.release_date) > new Date();
};

// Custom hook for audio element management
function useAudioElement(volume) {
  const audioRef = useRef(new Audio());
  
  useEffect(() => {
    audioRef.current.volume = volume;
  }, [volume]);
  
  return audioRef;
}

// Custom hook for playback state
function usePlaybackState() {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPreview, setIsPreview] = useState(false);
  
  return {
    currentTrack, setCurrentTrack,
    isPlaying, setIsPlaying,
    progress, setProgress,
    duration, setDuration,
    isPreview, setIsPreview
  };
}

// Custom hook for queue management
function useQueueState() {
  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(-1);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState('off'); // 'off', 'all', 'one'
  
  const toggleShuffle = useCallback(() => {
    setShuffle(prev => !prev);
  }, []);

  const toggleRepeat = useCallback(() => {
    setRepeat(prev => {
      if (prev === 'off') return 'all';
      if (prev === 'all') return 'one';
      return 'off';
    });
  }, []);

  const addToQueue = useCallback((track) => {
    setQueue(prev => [...prev, track]);
  }, []);

  const clearQueue = useCallback(() => {
    setQueue([]);
    setQueueIndex(-1);
  }, []);
  
  return {
    queue, setQueue,
    queueIndex, setQueueIndex,
    shuffle, repeat,
    toggleShuffle, toggleRepeat,
    addToQueue, clearQueue
  };
}

// Custom hook for volume control
function useVolumeControl(initialVolume = 0.7) {
  const [volume, setVolume] = useState(initialVolume);
  return { volume, setVolume };
}

export const PlayerProvider = ({ children }) => {
  const { volume, setVolume } = useVolumeControl(0.7);
  const audioRef = useAudioElement(volume);
  const playbackState = usePlaybackState();
  const queueState = useQueueState();
  const previewTimerRef = useRef(null);

  // Fire a GA4 music_play event — once per fresh playback session.
  // Dedup: we only invoke this from startTrack/playPreview (new src load).
  // Pause/resume uses audioRef.play() directly and will NOT fire here.
  const sendMusicPlayEvent = (track, { isPreview = false, isPaidPreview = false } = {}) => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function' || !track) return;
    try {
      window.gtag('event', 'music_play', {
        track_id: track.id || '',
        track_title: track.title || '',
        track_artist: track.artist || '',
        is_preview: !!isPreview,
        is_paid_preview: !!isPaidPreview,
      });
    } catch (_) { /* analytics must never break playback */ }
  };

  const {
    currentTrack, setCurrentTrack,
    isPlaying, setIsPlaying,
    progress, setProgress,
    duration, setDuration,
    isPreview, setIsPreview
  } = playbackState;

  // Starts playback of a track. Caps to 30-second preview for:
  //   1) unreleased tracks (release_date in future)
  //   2) paid tracks the user has NOT purchased (backend sends audio in `preview_url`)
  const startTrack = (track, { skipPlayCount = false } = {}) => {
    const isUnowned = !track.audio_url && !!track.preview_url;
    const playbackUrl = track.audio_url || track.preview_url;

    if (!playbackUrl) {
      toast.error('Purchase this track to listen');
      return;
    }

    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }

    const capped = isUnreleased(track) || isUnowned;
    audioRef.current.src = playbackUrl;
    setCurrentTrack(capped ? { ...track, isPreview: true, isPaidPreview: isUnowned } : track);
    setIsPreview(capped);
    const playPromise = audioRef.current.play();
    setIsPlaying(true);

    // GA4 music_play — fires once per fresh playback start, after audio actually begins.
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise
        .then(() => sendMusicPlayEvent(track, { isPreview: capped, isPaidPreview: isUnowned }))
        .catch(() => { /* autoplay/user-gesture errors — never fire music_play */ });
    } else {
      // Older browsers that don't return a Promise from play()
      sendMusicPlayEvent(track, { isPreview: capped, isPaidPreview: isUnowned });
    }

    if (!capped && !skipPlayCount) {
      axios.post(`${API_URL}/tracks/${track.id}/play`).catch(() => {});
    }

    if (capped) {
      previewTimerRef.current = setTimeout(() => {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setIsPlaying(false);
        setIsPreview(false);
        if (isUnowned) {
          // Sticky Buy Bar: keep the track visible in the bottom player
          // with `previewEnded` flag so the Player component can swap in a "Buy" CTA.
          setCurrentTrack({ ...track, previewEnded: true, isPaidPreview: true });
          toast('Preview ended — buy to unlock the full song', {
            description: track.title,
            action: track.id
              ? { label: 'Buy', onClick: () => { window.location.href = `/music/${track.id}`; } }
              : undefined,
            duration: 6000,
          });
        } else {
          setCurrentTrack(null);
        }
      }, PREVIEW_DURATION_MS);
    }
  };

  const {
    queue, setQueue,
    queueIndex, setQueueIndex,
    shuffle, repeat,
    toggleShuffle, toggleRepeat,
    addToQueue, clearQueue
  } = queueState;

  // Audio event listeners
  useEffect(() => {
    const audio = audioRef.current;

    const handleTimeUpdate = () => setProgress(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => {
      if (repeat === 'one') {
        audio.currentTime = 0;
        audio.play();
      } else {
        // Call playNext directly to avoid stale closure
        if (queue.length === 0) return;

        let nextIndex;
        if (shuffle) {
          nextIndex = Math.floor(Math.random() * queue.length);
        } else {
          nextIndex = queueIndex + 1;
          if (nextIndex >= queue.length) {
            if (repeat === 'all') {
              nextIndex = 0;
            } else {
              setIsPlaying(false);
              return;
            }
          }
        }

        setQueueIndex(nextIndex);
        const nextTrack = queue[nextIndex];
        if (nextTrack) {
          startTrack(nextTrack);
        }
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repeat, shuffle, queue, queueIndex, setProgress, setDuration, setIsPlaying, setQueueIndex, setCurrentTrack]);

  // Playback controls
  const playTrack = useCallback(async (track, trackList = null) => {
    if (trackList) {
      setQueue(trackList);
      const index = trackList.findIndex(t => t.id === track.id);
      setQueueIndex(index);
    }

    if (currentTrack?.id !== track.id) {
      startTrack(track);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  }, [currentTrack, setQueue, setQueueIndex, setIsPlaying, audioRef]);

  const pause = useCallback(() => {
    audioRef.current.pause();
    setIsPlaying(false);
  }, [setIsPlaying, audioRef]);

  const togglePlay = useCallback(() => {
    if (isPlaying) {
      pause();
    } else if (currentTrack) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  }, [isPlaying, currentTrack, pause, setIsPlaying, audioRef]);

  const seek = useCallback((time) => {
    audioRef.current.currentTime = time;
    setProgress(time);
  }, [setProgress, audioRef]);

  // Queue navigation
  const playNext = useCallback(() => {
    if (queue.length === 0) return;

    let nextIndex;
    if (shuffle) {
      nextIndex = Math.floor(Math.random() * queue.length);
    } else {
      nextIndex = queueIndex + 1;
      if (nextIndex >= queue.length) {
        if (repeat === 'all') {
          nextIndex = 0;
        } else {
          setIsPlaying(false);
          return;
        }
      }
    }

    setQueueIndex(nextIndex);
    const nextTrack = queue[nextIndex];
    if (nextTrack) {
      startTrack(nextTrack);
    }
  }, [queue, queueIndex, shuffle, repeat, setQueueIndex, setIsPlaying]);

  const playPrevious = useCallback(() => {
    if (queue.length === 0) return;

    if (audioRef.current.currentTime > 3) {
      audioRef.current.currentTime = 0;
      return;
    }

    let prevIndex = queueIndex - 1;
    if (prevIndex < 0) {
      if (repeat === 'all') {
        prevIndex = queue.length - 1;
      } else {
        audioRef.current.currentTime = 0;
        return;
      }
    }

    setQueueIndex(prevIndex);
    const prevTrack = queue[prevIndex];
    if (prevTrack) {
      startTrack(prevTrack);
    }
  }, [queue, queueIndex, repeat, setQueueIndex, audioRef]);

  // Preview functionality
  const playPreview = useCallback((track, durationSeconds = 30) => {
    if (!track.audio_url) {
      toast.error('Preview not available for this track yet');
      return;
    }

    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
    }

    audioRef.current.src = track.audio_url;
    setCurrentTrack({ ...track, isPreview: true });
    setIsPreview(true);
    
    const playPromise = audioRef.current.play();
    setIsPlaying(true);

    // GA4 music_play — fires once per fresh preview session.
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise
        .then(() => sendMusicPlayEvent(track, { isPreview: true }))
        .catch(() => {});
    } else {
      sendMusicPlayEvent(track, { isPreview: true });
    }

    previewTimerRef.current = setTimeout(() => {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setIsPreview(false);
      setCurrentTrack(null);
    }, durationSeconds * 1000);
  }, [setCurrentTrack, setIsPreview, setIsPlaying, audioRef]);

  // Cleanup preview timer
  useEffect(() => {
    return () => {
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
      }
    };
  }, []);

  // Memoize state values separately from functions
  const stateValues = useMemo(() => ({
    currentTrack, isPlaying, progress, duration, volume,
    queue, queueIndex, shuffle, repeat, isPreview
  }), [currentTrack, isPlaying, progress, duration, volume, queue, queueIndex, shuffle, repeat, isPreview]);

  // Memoize functions separately (these are already stable via useCallback)
  const actions = useMemo(() => ({
    playTrack, pause, togglePlay, seek, setVolume,
    playNext, playPrevious, toggleShuffle, toggleRepeat,
    addToQueue, clearQueue, playPreview
  }), [playTrack, pause, togglePlay, seek, setVolume, playNext, playPrevious, toggleShuffle, toggleRepeat, addToQueue, clearQueue, playPreview]);

  // Combine for context value
  const contextValue = useMemo(() => ({
    ...stateValues,
    ...actions
  }), [stateValues, actions]);

  return (
    <PlayerContext.Provider value={contextValue}>
      {children}
    </PlayerContext.Provider>
  );
};

export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
};
